/** @jsx jsx */
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	Fragment,
	createContext,
	type KeyboardEvent,
	type PropsWithChildren,
	useContext,
} from 'react';
import { jsx, css } from '@compiled/react';
import merge from 'lodash/merge';
import noop from 'lodash/noop';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { Box, Flex, Pressable, xcss } from '@atlaskit/primitives';
import Select, {
	PopupSelect,
	components,
	type MenuListComponentProps,
	type OptionProps,
} from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import type { AtlaskitSelectRefType } from '@atlaskit/select/types';
import type { SingleValue } from '@atlaskit/react-select';
import type { TriggerProps } from '@atlaskit/popup';
import Blanket from '@atlassian/jira-common-blanket/src/view.tsx';
import EnterEscapeHandler from '@atlassian/jira-common-components-enter-escape-handler/src/index.tsx';
import { statusCategoryForId } from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { GroupComponentWithStickyFooter } from '@atlassian/jira-select-with-footer-options/src/GroupComponentWithStickyFooter.tsx';
import type { FooterOption } from '@atlassian/jira-select-with-footer-options/src/utils.tsx';
import {
	AtlaskitSelectInlineTabActivator,
	type AtlaskitSelectInlineTabActivatorProps,
} from '@atlassian/jira-inline-config-buttons-for-select/src/AtlaskitSelectInlineTabActivator.tsx';
import { transformServerRestStatusToStatus } from '@atlassian/jira-status-config-common/src/utils/transformServerRestStatusToStatus.tsx';
import { OptionWithButtonStyle } from '@atlassian/jira-select-with-footer-options/src/OptionWithButtonStyle.tsx';
import { usePanelContext } from '@atlassian/entry-point-panel';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import {
	type StatusTransition,
	type StatusTransitionOption,
	type ModifierExtended,
	CREATE_STATUS_OPTION_TYPE,
	MANAGE_WORKFLOW_OPTION_TYPE,
	EDIT_STATUS_OPTION_TYPE,
	type TargetRefSetter,
} from '../../common/types.tsx';
import { WrappedStatusLozenge } from '../../common/ui/status-lozenge/index.tsx';
import {
	getFilteredOrderedTransitions,
	isOnlyGlobalTransitions,
	transformOptions,
} from '../../common/utils.tsx';
import { CommandPaletteStatusView } from './command-palette/index.tsx';
import { CreateStatusButton } from './configure-status/CreateStatusButton.tsx';
import { EditStatusButtonOld } from './configure-status/EditStatusButtonOld.tsx';
import messages from './messages.tsx';
import { LoadingSpinner } from './spinner/index.tsx';
import { StatusButton } from './status-button/index.tsx';
import { Transition } from './transition/index.tsx';
import type { Props, SelectTarget } from './types.tsx';
import {
	OptionComponent,
	useCombinedInlineConfigurationExperiment,
	useSelectOptions,
} from './utils.tsx';
import { OptionWithInlineConfigButtons } from './configure-status/OptionWithInlineConfigButtons.tsx';
import { CreateStatusOption } from './configure-status/CreateStatusOption.tsx';
import { EditStatusOption } from './configure-status/EditStatusOption.tsx';
import { EditStatusButton } from './configure-status/EditStatusButton.tsx';
import { ConfigureStatusPopup } from './configure-status/ConfigureStatusPopup.tsx';
import { ConfigureStatusPanel } from './configure-status/ConfigureStatusPanel.tsx';

const defaultPopperProps = {
	modifiers: [
		{
			name: 'offset',
			options: {
				offset: [0, 8],
			},
		},
		{
			name: 'preventOverflow',
			options: {
				padding: 5,
				rootBoundary: 'document',
			},
		},
	],
	// makes it work inside modal-dialog
	strategy: 'fixed',
};

const isOptionDisabled = (option: StatusTransitionOption | FooterOption) =>
	('transition' in option && option.transition.isDisabled) ?? false;

const isTransitionOption = (
	option: StatusTransitionOption | FooterOption | Pick<StatusTransitionOption, 'transition'>,
): option is StatusTransitionOption => 'transition' in option;

const isFooterOption = (option: StatusTransitionOption | FooterOption): option is FooterOption =>
	'type' in option;

const FormatTransitionOptionLabel = ({ option }: { option: StatusTransitionOption }) => {
	const {
		forceNoLabels = false,
		transitions,
		selectedTransition,
		showConfigureStatusButtons,
		configureStatusButtonsEnabled,
		projectId,
		projectKey,
		issueTypeId,
		onStatusCreatedOrUpdated,
		configureStatusSource,
		setIsConfigureStatusModalOpen,
		filteredAndOrderedTransitions,
	} = useStatusViewContext();
	const combinedInlineConfigurationExperiment = useCombinedInlineConfigurationExperiment(
		configureStatusButtonsEnabled,
	);
	const isGlobalOnly = isOnlyGlobalTransitions(filteredAndOrderedTransitions || []);
	const showLabel = !(forceNoLabels || isGlobalOnly);

	if (
		!fg('inline_config_a11y_improvements') &&
		(showConfigureStatusButtons || combinedInlineConfigurationExperiment.isEnabled) &&
		projectId &&
		projectKey
	) {
		const status = {
			id: option.transition.to.id,
			statusName: option.transition.to.name,
			statusCategory: statusCategoryForId(option.transition.to.statusCategory.id),
		};
		return (
			<Transition
				transition={option.transition}
				hasLabel={showLabel}
				contentAfter={
					// To be removed when FG jira-adoption-new-edit-status-flow-inline-config is cleaned up
					!fg('jira-adoption-new-edit-status-flow-inline-config') &&
					option.transition.id !== selectedTransition?.id && (
						<EditStatusButtonOld
							status={status}
							projectId={projectId}
							// issueTypeId is not immediately available on first render
							// temporary empty string will trigger a loading state within the edit status modal
							issueTypeId={issueTypeId ?? ''}
							projectKey={projectKey}
							statusNames={transitions?.map((transition) => transition.to.name)}
							onClick={() => setIsConfigureStatusModalOpen?.(true)}
							onClose={() => setIsConfigureStatusModalOpen?.(false)}
							onStatusUpdated={onStatusCreatedOrUpdated}
							source={configureStatusSource}
						/>
					)
				}
				testId="issue-field-status.ui.status-view.transition"
			/>
		);
	}

	return (
		<Box xcss={fg('jira_inline_config_option_spacing') ? transitionOptionStyles : undefined}>
			<Transition
				transition={option.transition}
				hasLabel={showLabel}
				testId="issue-field-status.ui.status-view.transition"
			/>
		</Box>
	);
};

const FormatTransitionOptionLabelForCommandPalette = ({
	option,
	transitionProps,
}: {
	option: Pick<StatusTransitionOption, 'transition'>;
	transitionProps?: {
		addTransitionArrowSpacing?: boolean;
	};
}) => {
	const {
		forceNoLabels = false,
		transitions,
		selectedTransition,
		showConfigureStatusButtons,
		configureStatusButtonsEnabled,
		projectId,
		projectKey,
		issueTypeId,
		onStatusCreatedOrUpdated,
		configureStatusSource,
		setIsConfigureStatusModalOpen,
		filteredAndOrderedTransitions,
	} = useStatusViewContext();
	const combinedInlineConfigurationExperiment = useCombinedInlineConfigurationExperiment(
		configureStatusButtonsEnabled,
	);
	const isGlobalOnly = isOnlyGlobalTransitions(filteredAndOrderedTransitions || []);
	const showLabel = !(forceNoLabels || isGlobalOnly);

	if (
		!fg('inline_config_a11y_improvements') &&
		(showConfigureStatusButtons || combinedInlineConfigurationExperiment.isEnabled) &&
		projectId &&
		projectKey
	) {
		const status = {
			id: option.transition.to.id,
			statusName: option.transition.to.name,
			statusCategory: statusCategoryForId(option.transition.to.statusCategory.id),
		};
		return (
			<Transition
				transition={option.transition}
				hasLabel={showLabel}
				addTransitionArrowSpacing={transitionProps?.addTransitionArrowSpacing}
				contentAfter={
					// To be removed when FG jira-adoption-new-edit-status-flow-inline-config is cleaned up
					!fg('jira-adoption-new-edit-status-flow-inline-config') &&
					option.transition.id !== selectedTransition?.id && (
						<EditStatusButtonOld
							status={status}
							projectId={projectId}
							// issueTypeId is not immediately available on first render
							// temporary empty string will trigger a loading state within the edit status modal
							issueTypeId={issueTypeId ?? ''}
							projectKey={projectKey}
							statusNames={transitions?.map((transition) => transition.to.name)}
							onClick={() => setIsConfigureStatusModalOpen?.(true)}
							onClose={() => setIsConfigureStatusModalOpen?.(false)}
							onStatusUpdated={onStatusCreatedOrUpdated}
							source={configureStatusSource}
						/>
					)
				}
				testId="issue-field-status.ui.status-view.transition"
			/>
		);
	}
	return (
		<Transition
			transition={option.transition}
			hasLabel={showLabel}
			addTransitionArrowSpacing={transitionProps?.addTransitionArrowSpacing}
			testId="issue-field-status.ui.status-view.transition"
		/>
	);
};

const FormatTransitionOptionLabelWithCreateStatus = ({
	option,
}: {
	option: StatusTransitionOption | FooterOption;
}) => {
	const {
		value,
		transitions,
		showConfigureStatusButtons,
		configureStatusButtonsEnabled,
		projectId,
		projectKey,
		projectType,
		issueTypeId,
		onClickConfigureStatus,
		onCloseConfigureStatus,
		onStatusCreatedOrUpdated,
		configureStatusSource,
		createStatusButtonRef,
		editStatusButtonRef,
		shouldStatusConfigTriggerModal,
	} = useStatusViewContext();
	const combinedInlineConfigurationExperiment = useCombinedInlineConfigurationExperiment(
		configureStatusButtonsEnabled,
	);
	const [inlineStatusConfigExp] = UNSAFE_noExposureExp('inline_status_add_and_rename_in_gic');
	const isInlineStatusExperimentEnabled = inlineStatusConfigExp.get(
		'isInlineStatusConfigEnabled',
		false,
	);
	const isConfigureStatusAvailable =
		isInlineStatusExperimentEnabled && showConfigureStatusButtons && projectType && projectKey;

	if (
		(isConfigureStatusAvailable || combinedInlineConfigurationExperiment.isEnabled) &&
		projectKey &&
		projectType &&
		projectId &&
		issueTypeId &&
		'value' in option
	) {
		switch (option.value) {
			case CREATE_STATUS_OPTION_TYPE:
				return fg('inline_config_a11y_improvements') ? (
					<CreateStatusOption
						projectId={projectId}
						projectKey={projectKey}
						projectType={projectType}
						issueTypeId={issueTypeId}
						statusNames={transitions?.map((transition) => transition?.to?.name)}
						onClick={onClickConfigureStatus}
						onClose={onCloseConfigureStatus}
						onStatusCreated={onStatusCreatedOrUpdated}
						buttonRef={createStatusButtonRef}
						source={configureStatusSource}
						shouldTriggerModal={shouldStatusConfigTriggerModal}
					/>
				) : (
					<CreateStatusButton
						projectId={projectId}
						projectKey={projectKey}
						projectType={projectType}
						issueTypeId={issueTypeId}
						statusNames={transitions?.map((transition) => transition?.to?.name)}
						onClick={onClickConfigureStatus}
						onClose={onCloseConfigureStatus}
						onStatusCreated={onStatusCreatedOrUpdated}
						isPressable
						buttonRef={createStatusButtonRef}
						source={configureStatusSource}
						shouldTriggerModal={shouldStatusConfigTriggerModal}
					/>
				);
			case EDIT_STATUS_OPTION_TYPE:
				return transitions && value && fg('jira-adoption-new-edit-status-flow-inline-config') ? (
					<EditStatusOption
						status={transformServerRestStatusToStatus(value)}
						statuses={transitions
							.map((transition) => transformServerRestStatusToStatus(transition?.to))
							.filter(
								(status, index, self) =>
									index === self.findIndex((t) => t.statusName === status.statusName),
							)}
						projectId={projectId}
						projectKey={projectKey}
						issueTypeId={issueTypeId}
						statusNames={transitions?.map((transition) => transition?.to?.name)}
						onClick={onClickConfigureStatus}
						onClose={onCloseConfigureStatus}
						onStatusUpdated={onStatusCreatedOrUpdated}
						source={configureStatusSource}
						buttonRef={editStatusButtonRef}
						shouldTriggerModal={shouldStatusConfigTriggerModal}
					/>
				) : (
					<></>
				);
			case MANAGE_WORKFLOW_OPTION_TYPE:
				return fg('inline_config_a11y_improvements') ? (
					<Box xcss={manageWorkflowStyles}>{option.label}</Box>
				) : (
					<Pressable xcss={manageWorkflowStyles}>{option.label}</Pressable>
				);
			default:
				break;
		}
	}
	if (isTransitionOption(option)) {
		return <FormatTransitionOptionLabel option={option} />;
	}
};

const CommandPaletteStatusViewComponent = () => {
	const {
		isLoading,
		value,
		transitions,
		onSuccess,
		onFetch,
		onUpdateStatusMutation,
		filteredAndOrderedTransitions,
	} = useStatusViewContext();
	const wasFetchSuccessful = transitions != null;

	return (
		<CommandPaletteStatusView
			transitions={filteredAndOrderedTransitions}
			currentStatus={value}
			onSuccess={onSuccess}
			wasFetchSuccessful={wasFetchSuccessful}
			renderTransition={(option, transitionProps) => (
				<FormatTransitionOptionLabelForCommandPalette
					option={option}
					transitionProps={transitionProps}
				/>
			)}
			onFetch={onFetch}
			isLoading={isLoading}
			onUpdateStatusMutation={onUpdateStatusMutation}
		/>
	);
};

const Lozenge = ({
	targetRef,
	triggerProps,
}: {
	targetRef?: TargetRefSetter;
	triggerProps?: TriggerProps;
}) => {
	const {
		isEditable = true,
		isSaving = false,
		error,
		value,
		statusButtonRef,
		isOpen,
	} = useStatusViewContext();
	const { formatMessage } = useIntl();
	if (isSaving) {
		return (
			<Blanket border={false}>
				<WrappedStatusLozenge
					targetRef={targetRef}
					status={value}
					error={error}
					contentRef={statusButtonRef}
					triggerProps={triggerProps}
				/>
			</Blanket>
		);
	}

	return (
		<WrappedStatusLozenge
			ariaLabel={`${value.name} - ${formatMessage(messages.changeStatus)}`}
			error={error}
			hasHoverState
			isEditable={isEditable}
			isOpen={isOpen}
			status={value}
			targetRef={targetRef}
			contentRef={statusButtonRef}
			triggerProps={triggerProps}
		/>
	);
};

const StatusButtonComponent = ({
	targetRef,
	triggerProps,
}: {
	targetRef?: TargetRefSetter;
	triggerProps?: TriggerProps;
}) => {
	const {
		isEditable = true,
		isSaving = false,
		value,
		statusButtonRef,
		shouldFitContainer = true,
		isOpen,
	} = useStatusViewContext();
	const { formatMessage } = useIntl();
	const statusButton = (
		<StatusButton
			ariaLabel={`${value.name} - ${formatMessage(messages.changeStatus)}`}
			isEditable={isEditable}
			isOpen={isOpen}
			status={value}
			targetRef={targetRef}
			statusButtonRef={statusButtonRef}
			shouldFitContainer={shouldFitContainer}
			triggerProps={triggerProps}
		/>
	);
	return isSaving ? <Blanket>{statusButton}</Blanket> : statusButton;
};

const FocusWrapper = ({ children }: PropsWithChildren) => {
	const { isSaving = false } = useStatusViewContext();
	const childWrapperRef = useRef<unknown>(null);
	const previousIsSaving = usePrevious(isSaving);

	useEffect(() => {
		if (previousIsSaving === true && isSaving === false) {
			childWrapperRef.current &&
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				childWrapperRef.current.children.length > 0 &&
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				childWrapperRef.current.children[0].focus();
		}
	}, [isSaving, previousIsSaving]);
	// @ts-expect-error - TS2322 - Type 'MutableRefObject<unknown>' is not assignable to type 'LegacyRef<HTMLDivElement> | undefined'.
	return <div ref={childWrapperRef}>{children}</div>;
};

const Target = ({
	targetRef,
	triggerProps,
}: {
	targetRef?: TargetRefSetter;
	triggerProps?: TriggerProps;
}) => {
	const { value, appearance, registerInCommandPalette } = useStatusViewContext();
	if (value) {
		if (appearance === 'lozenge') {
			return (
				<FocusWrapper>
					<Lozenge targetRef={targetRef} triggerProps={triggerProps} />
				</FocusWrapper>
			);
		}
		if (appearance === 'button') {
			return (
				<>
					<FocusWrapper>
						<StatusButtonComponent targetRef={targetRef} triggerProps={triggerProps} />
					</FocusWrapper>
					{registerInCommandPalette && <CommandPaletteStatusViewComponent />}
				</>
			);
		}
	}
	return <div />;
};

const OptionWithButtonForSelect = ({
	children,
	...optionProps
}: OptionProps<StatusTransitionOption | FooterOption>) => {
	const {
		projectId,
		projectKey,
		issueTypeId,
		transitions,
		onStatusCreatedOrUpdated,
		configureStatusSource,
		menuListRef,
		showConfigureStatusButtons,
		configureStatusButtonsEnabled,
		setIsConfigureStatusModalOpen,
	} = useStatusViewContext();

	const combinedInlineConfigurationExperiment = useCombinedInlineConfigurationExperiment(
		configureStatusButtonsEnabled,
	);

	if (
		isFooterOption(optionProps.data) &&
		[CREATE_STATUS_OPTION_TYPE, EDIT_STATUS_OPTION_TYPE, MANAGE_WORKFLOW_OPTION_TYPE].includes(
			optionProps.data.type,
		)
	) {
		return (
			<OptionWithButtonStyle
				{...optionProps}
				innerProps={{
					...optionProps.innerProps,
					role: 'button',
				}}
			>
				{children}
			</OptionWithButtonStyle>
		);
	}
	// To be removed when FG jira-adoption-new-edit-status-flow-inline-config is cleaned up
	if (
		projectId &&
		projectKey &&
		optionProps.data &&
		menuListRef &&
		isTransitionOption(optionProps.data) &&
		!fg('jira-adoption-new-edit-status-flow-inline-config')
	) {
		return (
			<OptionWithInlineConfigButtons
				showEditButton={
					combinedInlineConfigurationExperiment.isEnabled || Boolean(showConfigureStatusButtons)
				}
				projectId={projectId}
				projectKey={projectKey}
				// issueTypeId is not immediately available on first render
				// temporary empty string will trigger a loading state within the edit status modal
				issueTypeId={issueTypeId ?? ''}
				statusNames={transitions?.map((transition) => transition.to.name) ?? []}
				menuListRef={menuListRef}
				onStatusUpdated={onStatusCreatedOrUpdated}
				source={configureStatusSource}
				onOpenEditModal={() => setIsConfigureStatusModalOpen(true)}
				onCloseEditModal={() => setIsConfigureStatusModalOpen(false)}
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				{...(optionProps as OptionProps<StatusTransitionOption>)}
			>
				{children}
			</OptionWithInlineConfigButtons>
		);
	}

	return <OptionComponent {...optionProps}>{children}</OptionComponent>;
};

const Footer = () => {
	const {
		footer,
		showConfigureStatusButtons,
		configureStatusButtonsEnabled,
		value,
		transitions,
		onClickConfigureStatus,
		onCloseConfigureStatus,
		onStatusCreatedOrUpdated,
		createStatusButtonRef,
		editStatusButtonRef,
		configureStatusSource,
		shouldStatusConfigTriggerModal,
		projectKey,
		projectId,
		projectType,
		issueTypeId,
	} = useStatusViewContext();
	const { formatMessage } = useIntl();
	const combinedInlineConfigurationExperiment = useCombinedInlineConfigurationExperiment(
		configureStatusButtonsEnabled,
	);
	const showCreateStatusButton =
		(combinedInlineConfigurationExperiment.isEnabled || showConfigureStatusButtons) &&
		projectKey &&
		projectType &&
		projectId;

	const showEditStatusButton =
		showCreateStatusButton &&
		value &&
		transitions &&
		fg('jira-adoption-new-edit-status-flow-inline-config');

	if (!footer && !showCreateStatusButton) {
		return null;
	}

	const footerContent = (
		<>
			{showCreateStatusButton && (
				<CreateStatusButton
					projectId={projectId}
					projectKey={projectKey}
					projectType={projectType}
					// issueTypeId is not immediately available on first render
					// temporary empty string will trigger a loading state within the create status modal
					issueTypeId={issueTypeId ?? ''}
					statusNames={transitions?.map((transition) => transition?.to?.name)}
					onClick={onClickConfigureStatus}
					onClose={onCloseConfigureStatus}
					onStatusCreated={onStatusCreatedOrUpdated}
					buttonRef={createStatusButtonRef}
					source={configureStatusSource}
					shouldTriggerModal={shouldStatusConfigTriggerModal}
				/>
			)}
			{showEditStatusButton && (
				<EditStatusButton
					status={transformServerRestStatusToStatus(value)}
					statuses={transitions
						.map((transition) => transformServerRestStatusToStatus(transition?.to))
						.filter(
							(status, index, self) =>
								index === self.findIndex((t) => t.statusName === status.statusName),
						)}
					projectId={projectId}
					projectKey={projectKey}
					// issueTypeId is not immediately available on first render
					// temporary empty string will trigger a loading state within the edit status modal
					issueTypeId={issueTypeId ?? ''}
					statusNames={transitions?.map((transition) => transition?.to?.name)}
					onClick={onClickConfigureStatus}
					onClose={onCloseConfigureStatus}
					onStatusUpdated={onStatusCreatedOrUpdated}
					source={configureStatusSource}
					buttonRef={editStatusButtonRef}
					shouldTriggerModal={shouldStatusConfigTriggerModal}
				/>
			)}
			{footer}
		</>
	);

	return (
		<Flex direction="column" xcss={statusViewFooterStyles}>
			{fg('a11y_jira_issue_status_field') ? (
				<fieldset>
					<legend css={visuallyHiddenLegendStyles}>{formatMessage(messages.status)}</legend>
					{footerContent}
				</fieldset>
			) : (
				footerContent
			)}
		</Flex>
	);
};

type Context = Props & {
	menuListRef: React.RefObject<HTMLDivElement> | null;
	shouldStatusConfigTriggerModal: boolean;
	isOpen: boolean;
	setIsConfigureStatusModalOpen: (isOpen: boolean) => void;
	onClickConfigureStatus: () => void;
	onCloseConfigureStatus: () => void;
	createStatusButtonRef: React.RefObject<HTMLButtonElement> | null;
	editStatusButtonRef: React.RefObject<HTMLButtonElement> | null;
	filteredAndOrderedTransitions: StatusTransition[];
};

const StatusViewContext = createContext<Context>({
	isLoading: false,
	value: {
		id: '',
		name: '',
		statusCategory: { id: 0 },
	},
	transitions: [],
	appearance: 'button',
	shouldStatusConfigTriggerModal: true,
	isOpen: false,
	setIsConfigureStatusModalOpen: noop,
	onClickConfigureStatus: noop,
	onCloseConfigureStatus: noop,
	menuListRef: null,
	createStatusButtonRef: null,
	editStatusButtonRef: null,
	filteredAndOrderedTransitions: [],
});

const useStatusViewContext = () => {
	const context = useContext(StatusViewContext);
	if (!context) {
		throw new Error('useStatusViewContext must be used within a StatusViewProvider');
	}
	return context;
};

export const StatusView = (props: Props) => {
	const {
		isEditable = true,
		isSaving = false,
		isLoading,
		value,
		transitions,
		isDefaultSelect = false,
		selectedTransition,
		onChange = noop,
		onOpen = noop,
		onClose = noop,
		customNoOptionsMessage,
		statusButtonRef,
		popperProps,
		fieldId,
		showConfigureStatusButtons,
		configureStatusDialogType = 'modal',
		configureStatusPopupPlacement = 'bottom-end',
		configureStatusButtonsEnabled,
		projectId,
		projectKey,
		projectType,
		issueTypeId,
		// for testing
		defaultOpen = false,
		onStatusCreatedOrUpdated,
		onOpenManageWorkflow,
		configureStatusSource,
		autofocus = false,
	} = props;
	const [isOpen, setIsOpen] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const lastKey = useRef<string | null>(null);

	const selectRef = useRef<AtlaskitSelectRefType | null>(null);
	const wasChanged = useRef<boolean>(false);

	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const combinedInlineConfigurationExperiment = useCombinedInlineConfigurationExperiment(
		configureStatusButtonsEnabled,
	);

	const wasFetchSuccessful = transitions != null;
	const filteredAndOrderedTransitions = useMemo(
		() => getFilteredOrderedTransitions(transitions, value.id),
		[transitions, value.id],
	);

	const [inlineStatusConfigExp] = UNSAFE_noExposureExp('inline_status_add_and_rename_in_gic');
	const isInlineStatusExperimentEnabled = inlineStatusConfigExp.get(
		'isInlineStatusConfigEnabled',
		false,
	);
	const isConfigureStatusAvailable =
		isInlineStatusExperimentEnabled && showConfigureStatusButtons && projectType && projectKey;

	const createStatusButtonRef = useRef<HTMLButtonElement>(null);
	const editStatusButtonRef = useRef<HTMLButtonElement>(null);
	const menuListRef = useRef<HTMLDivElement>(null);

	const [isConfigureStatusModalOpen, setIsConfigureStatusModalOpen] = useState(false);
	const [isConfigureStatusPopupOpen, setIsConfigureStatusPopupOpen] = useState(false);
	const [shouldRefocus, setShouldRefocus] = useState(false);

	useEffect(() => {
		if (shouldRefocus && fg('jira_issue_view_status_config_modal_redesign')) {
			statusButtonRef?.current?.focus();
			setShouldRefocus(false);
		}
	}, [shouldRefocus, statusButtonRef]);

	const {
		Panel: ConfigureStatusPopupContent,
		Provider: ConfigureStatusPopupTriggerContextProvider,
	} = usePanelContext(ConfigureStatusPanel);

	const shouldRenderSelectComponent = isDefaultSelect && !!selectedTransition;
	const shouldStatusConfigTriggerModal =
		!fg('jira_issue_view_status_config_modal_redesign') ||
		configureStatusDialogType === 'modal' ||
		shouldRenderSelectComponent;

	const onCloseConfigureStatus = useCallback(() => {
		if (shouldStatusConfigTriggerModal) {
			setIsConfigureStatusModalOpen(false);
		} else {
			setIsConfigureStatusPopupOpen(false);
		}
		fg('jira_issue_view_status_config_modal_redesign') && setShouldRefocus(true);
	}, [shouldStatusConfigTriggerModal]);

	const onClickConfigureStatus = useCallback(() => {
		if (shouldStatusConfigTriggerModal) {
			setIsConfigureStatusModalOpen(true);
		} else {
			setIsConfigureStatusPopupOpen(true);
		}
	}, [shouldStatusConfigTriggerModal]);

	useEffect(() => {
		if (autofocus) {
			selectRef.current?.focus?.();
		}
	}, [autofocus]);

	// specifically for screen readers
	const getTransitionOptionLabel = (option: StatusTransitionOption | FooterOption) => {
		if (isTransitionOption(option)) {
			return option.transition.isGlobal
				? formatMessage(messages.transitionTo, { statusName: option.label })
				: `${option.transition.name} → ${option.transition.to.name}`;
		}
		return option.label;
	};

	const mergedPopperProps = useMemo(() => merge(defaultPopperProps, popperProps), [popperProps]);

	const handleChangeStatus = useCallback(
		(transition: StatusTransition) => {
			const { hasScreen } = transition;
			wasChanged.current = true;

			const analyticsEvent = createAnalyticsEvent({
				action: 'transitionSelected',
				actionSubject: 'statusField',
			});

			fireUIAnalytics(analyticsEvent, 'transitionSelectedStatusField', {
				hasScreen,
			});

			if (fg('obsrve-2239-traceid-errorflag-issue-transition')) {
				traceUFOPress('issue.fields.status.popup.submit');
			}

			onChange?.(transition, analyticsEvent);
		},
		[onChange, createAnalyticsEvent],
	);

	const handleChange = useCallback(
		(option: SingleValue<StatusTransitionOption | FooterOption>) => {
			if (
				(combinedInlineConfigurationExperiment.isEnabled || isConfigureStatusAvailable) &&
				option?.value &&
				[CREATE_STATUS_OPTION_TYPE, MANAGE_WORKFLOW_OPTION_TYPE, EDIT_STATUS_OPTION_TYPE].includes(
					option.value,
				)
			) {
				switch (option?.value) {
					case CREATE_STATUS_OPTION_TYPE:
						createStatusButtonRef?.current?.click();
						setIsConfigureStatusModalOpen(true);
						break;
					case EDIT_STATUS_OPTION_TYPE:
						if (fg('jira-adoption-new-edit-status-flow-inline-config')) {
							editStatusButtonRef?.current?.click();
							setIsConfigureStatusModalOpen(true);
						}
						break;
					case MANAGE_WORKFLOW_OPTION_TYPE:
						onOpenManageWorkflow && onOpenManageWorkflow();
						break;
					default:
						break;
				}
				return;
			}

			if (
				!fg('jira-adoption-new-edit-status-flow-inline-config') &&
				fg('inline_config_a11y_improvements')
			) {
				setIsMenuOpen(false);
			}

			if (option && isTransitionOption(option)) {
				handleChangeStatus(option.transition);
			}
		},
		[
			handleChangeStatus,
			isConfigureStatusAvailable,
			onOpenManageWorkflow,
			combinedInlineConfigurationExperiment.isEnabled,
		],
	);

	const handleOpen = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'openDropdown',
			actionSubject: 'statusField',
		});
		fireUIAnalytics(analyticsEvent, 'openDropdownStatusField');

		onOpen?.(analyticsEvent);
		setIsOpen(true);
		combinedInlineConfigurationExperiment.fireExposure();
	}, [onOpen, combinedInlineConfigurationExperiment, createAnalyticsEvent]);

	const handleClose = useCallback(() => {
		wasChanged.current = false;
		onClose?.();
		setIsOpen(false);
		fg('jira_issue_view_status_config_modal_redesign') && setShouldRefocus(true);
	}, [onClose]);

	const handleKeyDownForSelect = useCallback(
		(e: KeyboardEvent<HTMLElement>) => {
			lastKey.current = e.key;
		},
		[lastKey],
	);

	const handleCloseforSelect = useCallback(() => {
		// Prevents closing the menu when tabbing to edit status buttons
		// Preserves ability for Esc key to close menu
		if (lastKey.current !== 'Tab') {
			setIsMenuOpen(false);
		}
	}, [lastKey, setIsMenuOpen]);

	const isStatusInlineFooterEnabled = useMemo(
		() =>
			combinedInlineConfigurationExperiment.isEnabled ||
			(isInlineStatusExperimentEnabled && showConfigureStatusButtons),
		[
			combinedInlineConfigurationExperiment.isEnabled,
			isInlineStatusExperimentEnabled,
			showConfigureStatusButtons,
		],
	);
	const transformedOptions = useMemo(
		() => transformOptions(filteredAndOrderedTransitions || []),
		[filteredAndOrderedTransitions],
	);

	const selectOptions = useSelectOptions(transformedOptions, isStatusInlineFooterEnabled);

	const renderOptionWithButtonForPopupSelect = useCallback(
		({ children, ...optionProps }: OptionProps<StatusTransitionOption>) => {
			// To be removed when FG jira-adoption-new-edit-status-flow-inline-config is cleaned up
			if (
				projectId &&
				projectKey &&
				optionProps.data &&
				isTransitionOption(optionProps.data) &&
				!fg('jira-adoption-new-edit-status-flow-inline-config')
			) {
				return (
					<OptionWithInlineConfigButtons
						showEditButton={
							combinedInlineConfigurationExperiment.isEnabled || Boolean(showConfigureStatusButtons)
						}
						projectId={projectId}
						projectKey={projectKey}
						// issueTypeId is not immediately available on first render
						// temporary empty string will trigger a loading state within the edit status modal
						issueTypeId={issueTypeId ?? ''}
						statusNames={transitions?.map((transition) => transition.to.name) ?? []}
						menuListRef={menuListRef}
						onStatusUpdated={onStatusCreatedOrUpdated}
						source={configureStatusSource}
						onOpenEditModal={() => setIsConfigureStatusModalOpen(true)}
						onCloseEditModal={() => setIsConfigureStatusModalOpen(false)}
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						{...(optionProps as OptionProps<StatusTransitionOption>)}
					>
						{children}
					</OptionWithInlineConfigButtons>
				);
			}

			return <components.Option {...optionProps}>{children}</components.Option>;
		},
		[
			showConfigureStatusButtons,
			projectId,
			projectKey,
			issueTypeId,
			transitions,
			menuListRef,
			onStatusCreatedOrUpdated,
			configureStatusSource,
			combinedInlineConfigurationExperiment.isEnabled,
		],
	);

	const getNoOptionsMessage = () => {
		if (customNoOptionsMessage) {
			return customNoOptionsMessage;
		}
		const noPermissionMessage = fg('jira-issue-terminology-refresh-m3')
			? messages.noPermissionIssueTermRefresh
			: messages.noPermission;
		return formatMessage(wasFetchSuccessful ? noPermissionMessage : messages.cannotLoadTransitions);
	};

	const SelectWrapper =
		!fg('jira-adoption-new-edit-status-flow-inline-config') && fg('inline_config_a11y_improvements')
			? AtlaskitSelectInlineTabActivator
			: Fragment;

	const selectWrapperProps =
		!fg('jira-adoption-new-edit-status-flow-inline-config') && fg('inline_config_a11y_improvements')
			? {
					setIsMenuOpen,
					isMenuOpen,
				}
			: // Temporary until inline_config_a11y_improvements is cleaned up
				// Needed for VR tests to pass as props cannot be added to Fragment
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				({} as AtlaskitSelectInlineTabActivatorProps);

	const statusPopup = shouldRenderSelectComponent ? (
		<SelectWrapper {...selectWrapperProps}>
			<Select<StatusTransitionOption | FooterOption>
				label={formatMessage(messages.selectStatus)}
				inputId={fieldId}
				noOptionsMessage={getNoOptionsMessage}
				formatOptionLabel={(option) => (
					<FormatTransitionOptionLabelWithCreateStatus option={option} />
				)}
				getOptionLabel={getTransitionOptionLabel}
				options={selectOptions}
				isLoading={isLoading}
				isDisabled={!isEditable}
				isOptionDisabled={isOptionDisabled}
				onChange={handleChange}
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				value={transformOptions([selectedTransition!])[0]}
				isSearchable={false}
				onMenuOpen={handleOpen}
				closeMenuOnSelect
				{...(!fg('jira-adoption-new-edit-status-flow-inline-config') &&
				fg('inline_config_a11y_improvements')
					? {
							onKeyDown: handleKeyDownForSelect,
							onMenuClose: handleCloseforSelect,
						}
					: {})}
				components={{
					LoadingMessage: () => <LoadingSpinner />,
					...(isStatusInlineFooterEnabled && {
						Group: GroupComponentWithStickyFooter<StatusTransitionOption | FooterOption>,
						Option:
							!fg('jira-adoption-new-edit-status-flow-inline-config') &&
							fg('inline_config_a11y_improvements')
								? OptionWithButtonForSelect
								: OptionComponent,
					}),
					...(fg('inline_config_a11y_improvements') && {
						MenuList: ({
							children,
							...menuListProps
						}: MenuListComponentProps<StatusTransitionOption | FooterOption, false>) => (
							<components.MenuList {...menuListProps} innerRef={menuListRef}>
								{children}
							</components.MenuList>
						),
					}),
				}}
				menuIsOpen={
					// Remove eslint-disable-next-line when inline_config_a11y_improvements is cleaned up
					// eslint-disable-next-line no-nested-ternary
					isConfigureStatusModalOpen
						? false
						: !fg('jira-adoption-new-edit-status-flow-inline-config') &&
							  fg('inline_config_a11y_improvements')
							? isMenuOpen
							: undefined
				}
				popperProps={mergedPopperProps}
				ref={selectRef}
			/>
		</SelectWrapper>
	) : (
		<PopupSelect<StatusTransitionOption, false, ModifierExtended>
			inputId={fieldId}
			noOptionsMessage={getNoOptionsMessage}
			formatOptionLabel={(option) => <FormatTransitionOptionLabel option={option} />}
			getOptionLabel={getTransitionOptionLabel}
			options={
				fg('jira-adoption-new-edit-status-flow-inline-config')
					? transformedOptions
					: transformOptions(filteredAndOrderedTransitions || [])
			}
			target={({ ref }: SelectTarget) =>
				fg('jsc-1931_pass_custom_ref_to_status_view') ? (
					<Target targetRef={statusButtonRef ? mergeRefs(ref, statusButtonRef) : ref} />
				) : (
					<Target targetRef={ref} />
				)
			}
			isLoading={isLoading}
			isDisabled={!isEditable}
			isOptionDisabled={isOptionDisabled}
			searchThreshold={99999}
			onChange={handleChange}
			onOpen={handleOpen}
			handleClick={handleOpen}
			onClose={handleClose}
			closeMenuOnSelect
			components={{
				LoadingMessage: () => <LoadingSpinner />,
				...((combinedInlineConfigurationExperiment.isEnabled || showConfigureStatusButtons) &&
					fg('inline_config_a11y_improvements') && {
						Option: (optionProps: OptionProps<StatusTransitionOption>) =>
							renderOptionWithButtonForPopupSelect(optionProps),
					}),
				...(fg('inline_config_a11y_improvements') && {
					MenuList: ({
						children,
						...menuListProps
					}: MenuListComponentProps<StatusTransitionOption, false>) => (
						<components.MenuList {...menuListProps} innerRef={menuListRef}>
							{children}
						</components.MenuList>
					),
				}),
			}}
			isOpen={isConfigureStatusModalOpen ? false : undefined}
			popperProps={mergedPopperProps}
			footer={<Footer />}
			defaultIsOpen={defaultOpen}
			label={formatMessage(messages.selectStatus)}
			shouldCloseMenuOnTab={false}
		/>
	);

	const contextValues = useMemo(
		() => ({
			...props,
			menuListRef,
			shouldStatusConfigTriggerModal,
			isOpen,
			setIsConfigureStatusModalOpen,
			onClickConfigureStatus,
			onCloseConfigureStatus,
			createStatusButtonRef,
			editStatusButtonRef,
			filteredAndOrderedTransitions,
		}),
		[
			props,
			shouldStatusConfigTriggerModal,
			isOpen,
			setIsConfigureStatusModalOpen,
			onClickConfigureStatus,
			onCloseConfigureStatus,
			filteredAndOrderedTransitions,
		],
	);
	/* This prevents the dropdown from being accessible while saving/not editable,
	 * because for the underlying PopupSelect, the `isDisabled` prop only prevents
	 * the actual dropdown items from being selectable - the dropdown itself still
	 * pops up/down.
	 */
	if (isSaving || !isEditable) {
		return (
			<StatusViewContext.Provider value={contextValues}>
				<Target />
			</StatusViewContext.Provider>
		);
	}

	return (
		<StatusViewContext.Provider value={contextValues}>
			{isConfigureStatusPopupOpen ? (
				<ConfigureStatusPopup
					placement={configureStatusPopupPlacement}
					renderTarget={Target}
					renderPopupContent={() => <ConfigureStatusPopupContent />}
					isTargetEditable={isEditable}
					onClose={() => setIsConfigureStatusPopupOpen(false)}
				/>
			) : (
				<ConfigureStatusPopupTriggerContextProvider>
					<EnterEscapeHandler onEscape={noop}>{statusPopup}</EnterEscapeHandler>
				</ConfigureStatusPopupTriggerContextProvider>
			)}
		</StatusViewContext.Provider>
	);
};

const statusViewFooterStyles = xcss({
	borderTop: `2px solid ${token('color.border')}`,
});

const manageWorkflowStyles = xcss({
	height: '100%',
	width: '100%',
	backgroundColor: 'color.background.neutral.subtle',
	alignItems: 'center',
	textAlign: 'left',
	fontWeight: 'font.weight.medium',
});

const visuallyHiddenLegendStyles = css({
	clip: 'rect(1px, 1px, 1px, 1px)',
	height: '1px',
	width: '1px',
	overflow: 'hidden',
	padding: 0,
	position: 'absolute',
});

const transitionOptionStyles = xcss({
	paddingTop: 'space.025',
	paddingBottom: 'space.025',
});

export default StatusView;
